import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* =========================
   AUTH HEADER
========================= */
const authHeader = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  },
});

/* =========================
   ADMIN LOGIN
========================= */
export const adminLogin = createAsyncThunk(
  "adminUser/login",
  async (payload, thunkAPI) => {
    try {
      const res = await api.post("/api/adminuser/login", payload);

      localStorage.setItem("token", res.data.token);
      localStorage.setItem("adminId", res.data.userId);
      localStorage.setItem("role", res.data.role);  // now stores 1 or 2

      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Admin login failed"
      );
    }
  }
);

/* =========================
   FETCH ADMIN USERS
========================= */
export const fetchAdminUsers = createAsyncThunk(
  "adminUser/fetchAll",
  async (_, thunkAPI) => {
    try {
      const res = await api.get("/api/adminuser", authHeader());
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch admin users");
    }
  }
);

/* =========================
   CREATE ADMIN USER
========================= */
export const createAdminUser = createAsyncThunk(
  "adminUser/create",
  async (payload, thunkAPI) => {
    try {
      // ensure role is sent as number
      const data = { ...payload, role: Number(payload.role) };
      const res = await api.post("/api/adminuser", data, authHeader());
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to create admin user"
      );
    }
  }
);

/* =========================
   UPDATE ADMIN USER
========================= */
export const updateAdminUser = createAsyncThunk(
  "adminUser/update",
  async ({ id, data }, thunkAPI) => {
    try {
      // ensure role is sent as number
      const payload = { ...data, role: Number(data.role) };
      const res = await api.put(`/api/adminuser/${id}`, payload, authHeader());
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to update user"
      );
    }
  }
);

/* =========================
   DELETE ADMIN USER
========================= */
export const deleteAdminUser = createAsyncThunk(
  "adminUser/delete",
  async (id, thunkAPI) => {
    try {
      await api.delete(`/api/adminuser/${id}`, authHeader());
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to delete user");
    }
  }
);

/* =========================
   SLICE
========================= */
const adminUserSlice = createSlice({
  name: "adminUser",
  initialState: {
    list: [],
    token: localStorage.getItem("token") || null,
    role: Number(localStorage.getItem("role")) || null,  // store as number
    loading: false,
    success: false,
    error: null,
  },
  reducers: {
    resetStatus: (state) => {
      state.success = false;
      state.error = null;
    },
    adminLogout: (state) => {
      state.token = null;
      state.role = null;
      state.list = [];
      localStorage.clear();
    },
  },
  extraReducers: (builder) => {
    builder
      /* LOGIN */
      .addCase(adminLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.role = Number(action.payload.role);  // store as number
      })
      .addCase(adminLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* FETCH */
      .addCase(fetchAdminUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchAdminUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* CREATE */
      .addCase(createAdminUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAdminUser.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(createAdminUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* UPDATE */
      .addCase(updateAdminUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminUser.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateAdminUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* DELETE */
      .addCase(deleteAdminUser.fulfilled, (state, action) => {
        state.list = state.list.filter(
          (user) => user._id !== action.payload
        );
      });
  },
});

export const { resetStatus, adminLogout } = adminUserSlice.actions;
export default adminUserSlice.reducer;